import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Tabs,
  Tab,
  Box,
  Alert,
  Typography,
  Link,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { isAdmin as checkIsAdmin } from "../Utils/functions";

const AuthDialog = ({ open, onClose, initialTab = 0 }) => {
  const [tabValue, setTabValue] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login, register, user } = useAuth();
  const isAdmin = checkIsAdmin(user);
  const navigate = useNavigate();

  // Login form state - load email from localStorage
  const [loginEmail, setLoginEmail] = useState(() => {
    return localStorage.getItem("lastLoginEmail") || "";
  });
  const [loginPassword, setLoginPassword] = useState("");
  
  // Reference for password field to auto-focus
  const passwordInputRef = React.useRef(null);

  // Register form state
  const [registerUsername, setRegisterUsername] = useState("");
  const [registerDisplayName, setRegisterDisplayName] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState("");

  const handleTabChange = (event, newValue) => {
    // Prevent non-admins from accessing register tab
    if (newValue === 1 && !isAdmin) {
      setError("Only administrators can register new users");
      return;
    }
    setTabValue(newValue);
    setError("");
    // Clear password when switching tabs, but keep email
    setLoginPassword("");
    setRegisterUsername("");
    setRegisterDisplayName("");
    setRegisterEmail("");
    setRegisterPassword("");
    setRegisterConfirmPassword("");
  };

  const handleLogin = async () => {
    setError("");
    setLoading(true);

    if (!loginEmail || !loginPassword) {
      setError("Please fill in all fields");
      setLoading(false);
      // Auto-focus password field if email is filled
      if (loginEmail && !loginPassword) {
        setTimeout(() => passwordInputRef.current?.focus(), 100);
      }
      return;
    }

    // Store email in localStorage for next time
    localStorage.setItem("lastLoginEmail", loginEmail);

    const result = await login(loginEmail, loginPassword);

    if (result.success) {
      onClose();
      // Clear password but keep email
      setLoginPassword("");
    } else {
      setError(result.error);
      // Auto-focus password field on error
      setTimeout(() => passwordInputRef.current?.focus(), 100);
    }

    setLoading(false);
  };

  const handleRegister = async () => {
    setError("");
    setLoading(true);

    if (!registerUsername || !registerEmail || !registerPassword || !registerConfirmPassword) {
      setError("Please fill in all fields");
      setLoading(false);
      return;
    }

    if (registerPassword !== registerConfirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    if (registerPassword.length < 6) {
      setError("Password must be at least 6 characters long");
      setLoading(false);
      return;
    }

    const result = await register(registerUsername, registerEmail, registerPassword, registerDisplayName || undefined);

    if (result.success) {
      onClose();
      // Clear form
      setRegisterUsername("");
      setRegisterDisplayName("");
      setRegisterEmail("");
      setRegisterPassword("");
      setRegisterConfirmPassword("");
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  // Update tab when initialTab prop changes and load email from localStorage
  useEffect(() => {
    if (open) {
      // Load email from localStorage when dialog opens
      const savedEmail = localStorage.getItem("lastLoginEmail");
      if (savedEmail && !isAdmin) {
        setLoginEmail(savedEmail);
      }
      
      // If admin, default to register tab (index 0 since Login is hidden)
      if (isAdmin) {
        setTabValue(0); // Register tab is now at index 0 for admins
      } else {
        // If trying to open register tab but not admin, reset to login
        if (initialTab === 1 && !isAdmin) {
          setTabValue(0);
          setError("Only administrators can register new users");
        } else {
          setTabValue(initialTab);
        }
      }
      setError("");
    }
  }, [open, initialTab, isAdmin]);
  
  // Auto-focus password field when dialog opens and email is already filled
  useEffect(() => {
    if (open && !isAdmin && loginEmail && !loginPassword) {
      // Small delay to ensure the dialog is fully rendered
      setTimeout(() => {
        passwordInputRef.current?.focus();
      }, 100);
    }
  }, [open, isAdmin, loginEmail, loginPassword]);

  const handleClose = () => {
    setError("");
    setTabValue(0);
    // Clear password but keep email for next time
    setLoginPassword("");
    setRegisterUsername("");
    setRegisterDisplayName("");
    setRegisterEmail("");
    setRegisterPassword("");
    setRegisterConfirmPassword("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0, pt: 2, position: "relative" }}>
        <Tabs value={tabValue} onChange={handleTabChange} centered sx={{ minHeight: "48px" }}>
          {!isAdmin && <Tab label="Login" />}
          {isAdmin && <Tab label="Register" />}
        </Tabs>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isAdmin ? (
          // Register Form (for admins)
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Username"
              fullWidth
              required
              value={registerUsername}
              onChange={(e) => setRegisterUsername(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleRegister();
                }
              }}
            />
            <TextField
              label="Display Name (Optional)"
              fullWidth
              helperText="This is the name that will be shown when you're logged in"
              value={registerDisplayName}
              onChange={(e) => setRegisterDisplayName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleRegister();
                }
              }}
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              value={registerEmail}
              onChange={(e) => setRegisterEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleRegister();
                }
              }}
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              value={registerPassword}
              onChange={(e) => setRegisterPassword(e.target.value)}
              helperText="Must be at least 6 characters"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleRegister();
                }
              }}
            />
            <TextField
              label="Confirm Password"
              type="password"
              fullWidth
              value={registerConfirmPassword}
              onChange={(e) => setRegisterConfirmPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleRegister();
                }
              }}
            />
          </Box>
        ) : (
          // Login Form (for non-admins)
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mb: 1,
                textAlign: "center",
                fontStyle: "italic",
              }}
            >
              Need a login?{" "}
              <Link
                component="button"
                variant="body2"
                onClick={() => {
                  handleClose();
                  navigate("/requestquote");
                }}
                sx={{
                  color: "primary.main",
                  textDecoration: "underline",
                  cursor: "pointer",
                  "&:hover": {
                    color: "primary.dark",
                  },
                }}
              >
                Request a quote
              </Link>{" "}
              to receive access.
            </Typography>
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  // Auto-focus password field when Enter is pressed on email
                  passwordInputRef.current?.focus();
                }
              }}
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              inputRef={passwordInputRef}
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleLogin();
                }
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={isAdmin ? handleRegister : handleLogin}
          variant="contained"
          disabled={loading}
          sx={{ backgroundColor: "#5B7195", "&:hover": { backgroundColor: "#4a5d7a" } }}
        >
          {loading ? "Processing..." : isAdmin ? "Register" : "Login"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuthDialog;

